import { fromNullable } from 'maybeasy';
import { observer } from 'mobx-react';
import { Droppable } from '@hello-pangea/dnd';
import { ExperienceResourceArray, ProductCollectionResource } from '../../../Types';
import ExperienceSection from '../../ExperienceSection';
import NoExperiences from '../../NoExperiences';
import ProductCollectionDisplay from '../ProductCollectionDisplay';
import { useUnifiedDragContext } from '../UnifiedDragContext';
import DraggableGridItem from './DraggableGridItem';
import EmptyDropZone from '../Shared/EmptyDropZone';
import { getDragHighlightClass } from '../Shared/styles/dragStyles';
import { useExperienceMap } from '../Shared/hooks/useExperienceMap';

export interface Props {
  productCollection: ProductCollectionResource;
  experiencesArray: ExperienceResourceArray;
  index: number;
  totalSections: number;
  onOpenCatalog: () => void;
  editable?: boolean;
}

function Grid({
  productCollection,
  experiencesArray,
  index,
  totalSections,
  onOpenCatalog,
  editable = false,
}: Props) {
  const { experiences } = productCollection.payload;
  const { isDragging } = useUnifiedDragContext();

  const experienceMap = useExperienceMap(experiencesArray);

  return (
    <ExperienceSection
      productCollection={productCollection}
      index={index}
      totalSections={totalSections}
      onOpenCatalog={onOpenCatalog}
      editable={editable}
    >
      <ProductCollectionDisplay productCollection={productCollection}>
        <Droppable droppableId={`section-${index}-grid`} direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`mt-8 transition-colors ${
                experiences.length > 0
                  ? `grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 ${getDragHighlightClass(snapshot.isDraggingOver)}`
                  : ''
              }`}
            >
              {experiences.length > 0 ? (
                <>
                  {experiences.map((experienceId, idx) => (
                    <DraggableGridItem
                      key={experienceId}
                      experienceResource={fromNullable(experienceMap[experienceId])}
                      index={idx}
                      sectionIndex={index}
                      isDragging={isDragging}
                      editable={editable}
                    />
                  ))}
                </>
              ) : isDragging ? (
                <EmptyDropZone isDraggingOver={snapshot.isDraggingOver} />
              ) : (
                <NoExperiences onOpen={onOpenCatalog} />
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </ProductCollectionDisplay>
    </ExperienceSection>
  );
}

export default observer(Grid);
